import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { rutasGranelService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Filter, 
  Truck, 
  MapPin, 
  DollarSign, 
  Clock,
  Plus,
  Eye,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  BarChart3,
  Download,
  RefreshCw,
  Edit,
  Save,
  X,
  User
} from 'lucide-react';

// Interfaces
interface Registro {
  id: number;
  origen_ciudad: string;
  origen_departamento: string;
  destino_ciudad: string;
  destino_departamento: string;
  tipo_vehiculo: string;
  tipo_carga: string;
  configuracion: string;
  valor_sicetac: number;
  viajes_mes: number;
  ton_mes: number;
  comentarios: string;
  disponibilidad: string;
  valor_de_tarifa: number;
  categoria: string;
  ruta_completa: string;
  estado: string;
  objetivo: string; // Valor objetivo de % margen
  editado_por_mi?: boolean; // Indica si el proveedor actual editó esta fila
}

interface Estadisticas {
  total_rutas: number;
  total_disponibles: number;
  total_no_disponibles: number;
  promedio_valor_sicetac: number;
  ciudades_origen_lista: string[];
  ciudades_destino_lista: string[];
  tipos_vehiculos: Array<{tipo: string, count: number}>;
  tipos_carga: Array<{tipo: string, count: number}>;
  configuraciones: Array<{tipo: string, count: number}>;
}

// Styled Components
const RutasContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    text-align: center;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>` 
  background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.white : theme.colors.primary};
  color: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.textPrimary : theme.colors.white};
  border: ${({ theme, variant }) => variant === 'secondary' ? `1px solid ${theme.colors.gray300}` : 'none'};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.gray50 : theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Sección de filtros
const FiltersSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FiltersTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const FilterButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.white : theme.colors.primary};
  color: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.textPrimary : theme.colors.white};
  border: ${({ theme, variant }) => variant === 'secondary' ? `1px solid ${theme.colors.gray300}` : 'none'};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.gray50 : theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

// Tabla
const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
`;

const StickyHeaderCell = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.gray50};
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.gray50};
`;

const TableHeaderCell = styled.th<{ sortable?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
  
  ${({ sortable }) => sortable && `
    cursor: pointer;
    user-select: none;
    
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  `}
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: ${({ theme }) => theme.colors.gray50};
  }
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorBanner = styled.div`
  background: ${({ theme }) => theme.colors.error}15;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const SortIcon = styled.span`
  margin-left: ${({ theme }) => theme.spacing.xs};
  display: inline-flex;
  align-items: center;
`;

// Componentes para edición inline
const EditInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  font-size: 12px;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`;

const EditActionButton = styled.button<{ variant?: 'save' | 'cancel' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  margin: 0 2px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${({ variant, theme }) => {
    if (variant === 'save') {
      return `
        background: ${theme.colors.success};
        color: white;
        &:hover:not(:disabled) {
          background: ${theme.colors.success}dd;
        }
      `;
    } else if (variant === 'cancel') {
      return `
        background: ${theme.colors.error};
        color: white;
        &:hover:not(:disabled) {
          background: ${theme.colors.error}dd;
        }
      `;
    } else {
      return `
        background: ${theme.colors.primary};
        color: white;
        &:hover:not(:disabled) {
          background: ${theme.colors.primaryDark};
        }
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionsCell = styled.td`
  width: 100px;
  min-width: 100px;
  text-align: center;
  white-space: nowrap;
  padding: ${({ theme }) => theme.spacing.sm};
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const EditableCell = styled(TableCell)`
  padding: 4px 8px;
  vertical-align: top;
`;

const UserCell = styled(TableCell)`
  background: ${({ theme }) => theme.colors.primary}10;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  border-right: 2px solid ${({ theme }) => theme.colors.primary}30;
  white-space: nowrap;
  min-width: 120px;
  
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

// ⚠️ LICITACIÓN CERRADA - Cambiar a false para reabrir la edición
const LICITACION_CERRADA = true;

// Componente principal - Rutas Granel
const RutasGranelModule: React.FC = () => {
  // Obtener información del usuario autenticado
  const { proveedor } = useAuth();
  
  // Estados básicos
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState<Estadisticas | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Estados de paginación
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Estados de ordenamiento
  const [sorting, setSorting] = useState({
    sort_by: 'origen_ciudad',
    sort_order: 'ASC' as 'ASC' | 'DESC'
  });

  // Estados simples para filtros
  const [filtroOrigenCiudad, setFiltroOrigenCiudad] = useState('');
  const [filtroDestinoCiudad, setFiltroDestinoCiudad] = useState('');
  const [filtroTipoVehiculo, setFiltroTipoVehiculo] = useState('');
  const [filtroTipoCarga, setFiltroTipoCarga] = useState('');
  const [filtroConfiguracion, setFiltroConfiguracion] = useState('');

  // Estados para opciones de filtros (se extraen de los registros cargados)
  const [opcionesFiltros, setOpcionesFiltros] = useState({
    ciudades_origen: [] as string[],
    ciudades_destino: [] as string[],
    tipos_vehiculos: [] as string[],
    tipos_carga: [] as string[],
    configuraciones: [] as string[]
  });

  // Estados para edición inline
  const [editingRow, setEditingRow] = useState<Registro | null>(null);
  const [editForm, setEditForm] = useState({
    porcentaje_margen: '',
    valor_tarifa_directo: '', // Para cuando no hay valor SICETAC
    comentarios: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Calcular el valor de tarifa basado en el porcentaje de margen
  const calcularValorTarifa = (valorSicetac: number, porcentajeMargen: number): number => {
    return valorSicetac * (1 + porcentajeMargen / 100);
  };

  // Función simple para cargar datos
  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null); // Limpiar error anterior
      console.log('🔄 Cargando datos...');
      
      const params: any = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };

      // Agregar username del proveedor para el LEFT JOIN
      if (proveedor?.username) {
        params.proveedor = proveedor.username;
        console.log('👤 Cargando datos para proveedor:', proveedor.username);
      }

      // Agregar filtros solo si tienen valor
      if (filtroOrigenCiudad) params.origen_ciudad = filtroOrigenCiudad;
      if (filtroDestinoCiudad) params.destino_ciudad = filtroDestinoCiudad;
      if (filtroTipoVehiculo) params.tipo_vehiculo = filtroTipoVehiculo;
      if (filtroTipoCarga) params.tipo_carga = filtroTipoCarga;
      if (filtroConfiguracion) params.configuracion = filtroConfiguracion;

      console.log('📡 Parámetros de consulta:', params);

      const response = await rutasGranelService.getTablaCompleta(params);
      const data = response.data;

      if (data.registros && Array.isArray(data.registros)) {
        setRegistros(data.registros);
        console.log('✅ Registros cargados:', data.registros.length);
        
        // Extraer opciones únicas para filtros de los datos cargados
        actualizarOpcionesFiltros(data.registros);
        setError(null); // Limpiar cualquier error previo
      } else {
        // Si no hay registros en la respuesta pero fue exitosa
        setRegistros([]);
        setError(null);
      }

      if (data.pagination) {
        setPagination(data.pagination);
      }

    } catch (error: any) {
      console.error('❌ Error cargando datos:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al cargar los datos';
      setError(errorMessage);
      // NO borrar registros aquí - mantener los datos anteriores
      // setRegistros([]); // ❌ ESTO CAUSABA EL PROBLEMA
    } finally {
      setLoading(false);
    }
  };

  // Función para extraer opciones únicas de los registros
  const actualizarOpcionesFiltros = (registros: Registro[]) => {
    const ciudadesOrigen = Array.from(new Set(registros.map(r => r.origen_ciudad).filter(Boolean))).sort();
    const ciudadesDestino = Array.from(new Set(registros.map(r => r.destino_ciudad).filter(Boolean))).sort();
    const tiposVehiculos = Array.from(new Set(registros.map(r => r.tipo_vehiculo).filter(Boolean))).sort();
    const tiposCarga = Array.from(new Set(registros.map(r => r.tipo_carga).filter(Boolean))).sort();
    const configuraciones = Array.from(new Set(registros.map(r => r.configuracion).filter(Boolean))).sort();

    setOpcionesFiltros({
      ciudades_origen: ciudadesOrigen,
      ciudades_destino: ciudadesDestino,
      tipos_vehiculos: tiposVehiculos,
      tipos_carga: tiposCarga,
      configuraciones: configuraciones
    });

    console.log('📊 Opciones de filtros actualizadas:', {
      ciudades_origen: ciudadesOrigen.length,
      ciudades_destino: ciudadesDestino.length,
      tipos_vehiculos: tiposVehiculos.length,
      tipos_carga: tiposCarga.length,
      configuraciones: configuraciones.length
    });
  };

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []); // Solo al montar el componente

  // Funciones de manejo
  const handleSort = (column: string) => {
    const newOrder = sorting.sort_by === column && sorting.sort_order === 'ASC' ? 'DESC' : 'ASC';
    setSorting({ sort_by: column, sort_order: newOrder });
    // Recargar datos con nuevo ordenamiento
    cargarDatos();
  };

  const handleFilterSubmit = () => {
    console.log('🔍 Aplicando filtros...');
    setPagination(prev => ({ ...prev, current_page: 1 })); // Resetear a página 1
    cargarDatos();
  };

  const handleClearFilters = () => {
    console.log('🧹 Limpiando filtros...');
    setFiltroOrigenCiudad('');
    setFiltroDestinoCiudad('');
    setFiltroTipoVehiculo('');
    setFiltroTipoCarga('');
    setFiltroConfiguracion('');
    setPagination(prev => ({ ...prev, current_page: 1 })); // Resetear a página 1
    // Los datos se recargarán automáticamente al cambiar los estados
    setTimeout(() => cargarDatos(), 100); // Pequeño delay para que se actualicen los estados
  };

  const handlePageChange = (newPage: number) => {
    console.log(`📄 Cambiando a página ${newPage}`);
    setPagination(prev => ({ ...prev, current_page: newPage }));
    
    // Cargar datos inmediatamente con la nueva página
    cargarDatosConPagina(newPage);
  };

  // Función auxiliar para cargar datos con una página específica
  const cargarDatosConPagina = async (pageNumber: number = pagination.current_page) => {
    try {
      setLoading(true);
      setError(null); // Limpiar error anterior
      console.log(`🔄 Cargando datos para página ${pageNumber}...`);
      
      const params: any = {
        page: pageNumber,
        per_page: pagination.per_page,
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };

      // Agregar username del proveedor para el LEFT JOIN
      if (proveedor?.username) {
        params.proveedor = proveedor.username;
      }

      // Agregar filtros solo si tienen valor
      if (filtroOrigenCiudad) params.origen_ciudad = filtroOrigenCiudad;
      if (filtroDestinoCiudad) params.destino_ciudad = filtroDestinoCiudad;
      if (filtroTipoVehiculo) params.tipo_vehiculo = filtroTipoVehiculo;
      if (filtroTipoCarga) params.tipo_carga = filtroTipoCarga;
      if (filtroConfiguracion) params.configuracion = filtroConfiguracion;

      console.log('📡 Parámetros de consulta:', params);

      const response = await rutasGranelService.getTablaCompleta(params);
      const data = response.data;

      if (data.registros && Array.isArray(data.registros)) {
        setRegistros(data.registros);
        console.log(`✅ Registros cargados para página ${pageNumber}:`, data.registros.length);
        
        // Extraer opciones únicas para filtros de los datos cargados
        actualizarOpcionesFiltros(data.registros);
        setError(null); // Limpiar cualquier error previo
      } else {
        // Si no hay registros en la respuesta pero fue exitosa
        setRegistros([]);
        setError(null);
      }

      if (data.pagination) {
        setPagination(data.pagination);
      }

    } catch (error: any) {
      console.error('❌ Error cargando datos:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al cargar los datos';
      setError(errorMessage);
      // NO borrar registros aquí - mantener los datos anteriores
      // setRegistros([]); // ❌ ESTO CAUSABA EL PROBLEMA
    } finally {
      setLoading(false);
    }
  };

  const getSortIcon = (column: string) => {
    if (sorting.sort_by !== column) return null;
    return sorting.sort_order === 'ASC' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  // Funciones de edición
  const handleEditStart = (registro: Registro) => {
    console.log('🖊️ Iniciando edición para registro:', registro.id);
    setEditingRow(registro);
    
    // Si hay valor SICETAC, calcular el porcentaje de margen
    // Si no hay valor SICETAC, usar el valor de tarifa directo
    const tieneValorSicetac = registro.valor_sicetac && registro.valor_sicetac > 0;
    const porcentajeCalculado = tieneValorSicetac && registro.valor_de_tarifa
      ? ((registro.valor_de_tarifa / registro.valor_sicetac - 1) * 100).toFixed(2)
      : '0';
    
    setEditForm({
      porcentaje_margen: porcentajeCalculado,
      valor_tarifa_directo: !tieneValorSicetac && registro.valor_de_tarifa ? registro.valor_de_tarifa.toString() : '',
      comentarios: registro.comentarios || ''
    });
  };

  const handleEditCancel = () => {
    console.log('❌ Cancelando edición');
    setEditingRow(null);
    setEditForm({
      porcentaje_margen: '',
      valor_tarifa_directo: '',
      comentarios: ''
    });
  };

  const handleEditSave = async () => {
    if (!editingRow || !proveedor) return;

    try {
      setIsUpdating(true);
      console.log('💾 Guardando cambios para registro:', editingRow.id);

      const valorSicetac = editingRow.valor_sicetac || 0;
      const tieneValorSicetac = valorSicetac > 0;
      
      let valorTarifaFinal: number;
      let porcentajeMargen: number;

      if (tieneValorSicetac) {
        // Caso normal: calcular tarifa a partir del porcentaje
        porcentajeMargen = parseFloat(editForm.porcentaje_margen) || 0;

        // Validar que el porcentaje sea válido
        if (isNaN(porcentajeMargen)) {
          alert(
            `❌ ERROR DE VALIDACIÓN\n\n` +
            `El % de Margen debe ser un número válido.\n\n` +
            `Por favor, ingresa un porcentaje válido (puede ser negativo para descuento).`
          );
          setIsUpdating(false);
          return;
        }

        // Calcular el valor de tarifa basado en el porcentaje de margen
        valorTarifaFinal = calcularValorTarifa(valorSicetac, porcentajeMargen);

        // Validar que el margen no sea tan negativo que el valor quede en 0 o negativo
        if (valorTarifaFinal <= 0) {
          alert(
            `❌ ERROR DE VALIDACIÓN\n\n` +
            `El % de Margen es demasiado bajo.\n\n` +
            `Valor SICETAC: $${(valorSicetac || 0).toLocaleString()}\n` +
            `% Margen ingresado: ${porcentajeMargen}%\n` +
            `Valor Tarifa resultante: $${(valorTarifaFinal || 0).toLocaleString()}\n\n` +
            `El valor de tarifa debe ser mayor a $0.`
          );
          setIsUpdating(false);
          return;
        }
      } else {
        // Caso sin SICETAC: usar el valor de tarifa directo
        valorTarifaFinal = parseFloat(editForm.valor_tarifa_directo) || 0;
        porcentajeMargen = 0; // No hay porcentaje cuando no hay SICETAC

        // Validar que se haya ingresado un valor
        if (valorTarifaFinal <= 0) {
          alert(
            `❌ ERROR DE VALIDACIÓN\n\n` +
            `Debe ingresar un Valor de Tarifa válido mayor a $0.`
          );
          setIsUpdating(false);
          return;
        }
      }

      // Datos para guardar en SICETAC_TARIFAS_PROVEEDORES_GRANEL
      const propuestaData = {
        proveedor: proveedor.username || proveedor.razon_social || 'Usuario',
        origen_ciudad: editingRow.origen_ciudad,
        destino_ciudad: editingRow.destino_ciudad,
        tipo_vehiculo: editingRow.tipo_vehiculo,
        tipo_carga: editingRow.tipo_carga,
        configuracion: editingRow.configuracion,
        valor_sicetac: editingRow.valor_sicetac || 0,
        valor_de_tarifa: valorTarifaFinal,
        porcentaje: porcentajeMargen,
        comentarios: editForm.comentarios.trim()
      };

      console.log('📡 Enviando propuesta del proveedor:', propuestaData);

      const response = await rutasGranelService.guardarPropuestaProveedor(propuestaData);
      
      if (response.status === 200) {
        console.log('✅ Propuesta guardada exitosamente:', response.data);
        
        // Actualizar el registro en el estado local (interfaz)
        const updatedRegistros = registros.map(registro => 
          registro.id === editingRow.id 
            ? { 
                ...registro, 
                valor_de_tarifa: valorTarifaFinal,
                comentarios: editForm.comentarios.trim()
              }
            : registro
        );
        
        setRegistros(updatedRegistros);
        
        // Limpiar estado de edición
        setEditingRow(null);
        setEditForm({
          porcentaje_margen: '',
          valor_tarifa_directo: '',
          comentarios: ''
        });

        // Mostrar mensaje de éxito
        const valorPropuesto = response.data.valor_propuesto || valorTarifaFinal || 0;
        alert(`✅ ${response.data.message}\n\nProveedor: ${response.data.proveedor}\nRuta: ${response.data.ruta}\n% Margen: ${porcentajeMargen}%\nValor SICETAC: $${(valorSicetac || 0).toLocaleString()}\nValor Propuesto: $${valorPropuesto.toLocaleString()}`);
        console.log('🎉 Propuesta guardada exitosamente');
        
      } else {
        console.error('❌ Error en la respuesta:', response);
        alert('Error al guardar la propuesta. Por favor, inténtelo de nuevo.');
      }

    } catch (error: any) {
      console.error('❌ Error guardando propuesta:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Error desconocido';
      alert(`Error al guardar la propuesta: ${errorMsg}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditFormChange = (field: 'porcentaje_margen' | 'valor_tarifa_directo' | 'comentarios', value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Estado para controlar la descarga
  const [isDownloading, setIsDownloading] = useState(false);

  // Función para descargar TODOS los registros de la tabla como archivo Excel (CSV)
  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      console.log('📥 Iniciando descarga de TODOS los registros...');

      // Preparar parámetros para obtener TODOS los registros (sin límite de paginación)
      const params: any = {
        page: 1,
        per_page: 100000, // Número muy alto para obtener todos los registros
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };

      // Agregar username del proveedor para el LEFT JOIN
      if (proveedor?.username) {
        params.proveedor = proveedor.username;
      }

      // Aplicar los mismos filtros que están activos
      if (filtroOrigenCiudad) params.origen_ciudad = filtroOrigenCiudad;
      if (filtroDestinoCiudad) params.destino_ciudad = filtroDestinoCiudad;
      if (filtroTipoVehiculo) params.tipo_vehiculo = filtroTipoVehiculo;
      if (filtroTipoCarga) params.tipo_carga = filtroTipoCarga;
      if (filtroConfiguracion) params.configuracion = filtroConfiguracion;

      console.log('📡 Solicitando todos los registros con filtros:', params);

      // Hacer llamada al API para obtener TODOS los registros
      const response = await rutasGranelService.getTablaCompleta(params);
      const todosLosRegistros = response.data.registros || [];

      if (todosLosRegistros.length === 0) {
        alert('No hay datos para descargar');
        setIsDownloading(false);
        return;
      }

      console.log(`📊 Total de registros obtenidos: ${todosLosRegistros.length}`);

      // Definir las columnas para el Excel
      const headers = [
        'Usuario',
        'Ciudad Origen',
        'Ciudad Destino',
        'Tipo Vehículo',
        'Tipo Carga',
        'Configuración',
        'Valor SICETAC',
        'Frecuencia mes',
        'Toneladas mes',
        '% Margen',
        'Objetivo',
        'Valor Tarifa',
        'Comentarios'
      ];

      // Preparar los datos de TODOS los registros
      const data = todosLosRegistros.map((registro: Registro) => {
        // Calcular el porcentaje de margen
        const porcentajeMargen = registro.valor_de_tarifa && registro.valor_sicetac 
          ? ((registro.valor_de_tarifa / registro.valor_sicetac - 1) * 100).toFixed(2)
          : '0';

        return [
          proveedor?.username || proveedor?.razon_social || 'Usuario',
          registro.origen_ciudad || '',
          registro.destino_ciudad || '',
          registro.tipo_vehiculo || '',
          registro.tipo_carga || '',
          registro.configuracion || '',
          registro.valor_sicetac?.toString() || '0',
          registro.viajes_mes?.toString() || '',
          registro.ton_mes?.toString() || '',
          `${porcentajeMargen}%`,
          registro.objetivo ? (() => {
            const val = parseFloat(registro.objetivo);
            if (isNaN(val)) return '-';
            const porcentaje = val < 1 ? (val * 100).toFixed(2) : val.toFixed(2);
            return `${porcentaje}%`;
          })() : '-',
          registro.valor_de_tarifa?.toString() || '0',
          registro.comentarios || ''
        ];
      });

      // Crear contenido CSV con BOM para Excel (soporte de caracteres especiales)
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(';'),  // Usar punto y coma para mejor compatibilidad con Excel en español
        ...data.map((row: string[]) => row.map(cell => {
          // Escapar comillas dobles y encerrar en comillas si contiene caracteres especiales
          const cellStr = String(cell).replace(/"/g, '""');
          return `"${cellStr}"`;
        }).join(';'))
      ].join('\r\n');

      // Crear el blob y descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Generar nombre del archivo con fecha
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Tabla_Tarifas_COMPLETA_${fecha}.csv`;
      
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Archivo descargado: ${nombreArchivo} (${todosLosRegistros.length} registros)`);
      alert(`✅ Descarga completada!\n\nArchivo: ${nombreArchivo}\nTotal de registros: ${todosLosRegistros.length}`);

    } catch (error: any) {
      console.error('❌ Error al descargar:', error);
      alert(`Error al descargar los datos: ${error.message || 'Error desconocido'}`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading && registros.length === 0) {
    return (
      <RutasContainer>
        <LoadingSpinner>
          <RefreshCw className="animate-spin" size={24} />
          <span style={{ marginLeft: '12px' }}>Cargando tabla de tarifas...</span>
        </LoadingSpinner>
      </RutasContainer>
    );
  }

  return (
    <RutasContainer>
      <Header>
        <Title>Rutas Granel</Title>
        <HeaderActions>
          <ActionButton variant="secondary" onClick={handleDownloadExcel} disabled={isDownloading}>
            {isDownloading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download size={16} />
                Descargar Excel
              </>
            )}
          </ActionButton>
          <ActionButton variant="secondary" onClick={() => cargarDatos()}>
            <RefreshCw size={16} />
            Actualizar
          </ActionButton>
        </HeaderActions>
      </Header>

      {/* Sección de filtros */}
      <FiltersSection>
        <FiltersTitle>
          <Filter size={20} />
          Filtros de Búsqueda
        </FiltersTitle>
        
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Ciudad Origen</FilterLabel>
            <FilterSelect
              value={filtroOrigenCiudad}
              onChange={(e) => setFiltroOrigenCiudad(e.target.value)}
            >
              <option value="">Todas las ciudades origen</option>
              {opcionesFiltros.ciudades_origen.map((ciudad) => (
                <option key={ciudad} value={ciudad}>
                  {ciudad}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Ciudad Destino</FilterLabel>
            <FilterSelect
              value={filtroDestinoCiudad}
              onChange={(e) => setFiltroDestinoCiudad(e.target.value)}
            >
              <option value="">Todas las ciudades destino</option>
              {opcionesFiltros.ciudades_destino.map((ciudad) => (
                <option key={ciudad} value={ciudad}>
                  {ciudad}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo de Vehículo</FilterLabel>
            <FilterSelect
              value={filtroTipoVehiculo}
              onChange={(e) => setFiltroTipoVehiculo(e.target.value)}
            >
              <option value="">Todos los tipos</option>
              {opcionesFiltros.tipos_vehiculos.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {tipo}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo de Carga</FilterLabel>
            <FilterSelect
              value={filtroTipoCarga}
              onChange={(e) => setFiltroTipoCarga(e.target.value)}
            >
              <option value="">Todos los tipos</option>
              {opcionesFiltros.tipos_carga.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {tipo}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Configuración</FilterLabel>
            <FilterSelect
              value={filtroConfiguracion}
              onChange={(e) => setFiltroConfiguracion(e.target.value)}
            >
              <option value="">Todas las configuraciones</option>
              {opcionesFiltros.configuraciones.map((config) => (
                <option key={config} value={config}>
                  {config}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>

        <FilterActions>
          <FilterButton variant="secondary" onClick={handleClearFilters}>
            <X size={16} />
            Limpiar Filtros
          </FilterButton>
          <FilterButton onClick={handleFilterSubmit}>
            <Search size={16} />
            Aplicar Filtros
          </FilterButton>
        </FilterActions>
      </FiltersSection>

      {/* Banner de licitación cerrada */}
      {LICITACION_CERRADA && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 4px 6px rgba(245, 158, 11, 0.15)'
        }}>
          <AlertCircle size={24} style={{ color: '#d97706', flexShrink: 0 }} />
          <div>
            <strong style={{ color: '#92400e', fontSize: '16px' }}>🔒 Licitación Cerrada</strong>
            <p style={{ color: '#78350f', margin: '4px 0 0 0', fontSize: '14px' }}>
              El período de propuestas ha finalizado. Puede consultar sus datos pero ya no es posible realizar modificaciones.
            </p>
          </div>
        </div>
      )}

      {/* Mensaje informativo sobre objetivos */}
      <div style={{
        backgroundColor: 'rgba(244, 114, 182, 0.15)',
        border: '1px solid rgba(244, 114, 182, 0.4)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginTop: '16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <AlertCircle size={20} style={{ color: '#db2777', flexShrink: 0 }} />
        <span style={{ color: '#831843', fontSize: '14px' }}>
          <strong>¡Atención!</strong> Para algunas rutas con un valor objetivo definido, se recomienda que la tarifa propuesta no exceda dicho objetivo.
        </span>
      </div>

      {/* Banner de error */}
      {error && (
        <ErrorBanner>
          <AlertCircle size={20} />
          <span>Error: {error}</span>
          <button 
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={16} />
          </button>
        </ErrorBanner>
      )}

      {/* Tabla de datos */}
      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                borderRight: '2px solid rgba(255,255,255,0.3)',
                fontWeight: 'bold'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={18} />
                  Usuario
                </div>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('origen_ciudad')}>
                Ciudad Origen <SortIcon>{getSortIcon('origen_ciudad')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('destino_ciudad')}>
                Ciudad Destino <SortIcon>{getSortIcon('destino_ciudad')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('tipo_vehiculo')}>
                Vehículo <SortIcon>{getSortIcon('tipo_vehiculo')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('tipo_carga')}>
                Carga <SortIcon>{getSortIcon('tipo_carga')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('configuracion')}>
                Config <SortIcon>{getSortIcon('configuracion')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('valor_sicetac')}>
                Valor SICETAC <SortIcon>{getSortIcon('valor_sicetac')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('viajes_mes')}>
                Frecuencia mes <SortIcon>{getSortIcon('viajes_mes')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('ton_mes')}>
                Toneladas mes <SortIcon>{getSortIcon('ton_mes')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell>
                % Margen
              </TableHeaderCell>
              <TableHeaderCell>
                Objetivo
              </TableHeaderCell>
              <TableHeaderCell sortable onClick={() => handleSort('VALOR_DE_TARIFA')}>
                Valor Tarifa <SortIcon>{getSortIcon('VALOR_DE_TARIFA')}</SortIcon>
              </TableHeaderCell>
              <TableHeaderCell>Comentarios</TableHeaderCell>
              <StickyHeaderCell>Acciones</StickyHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {registros.length === 0 ? (
              <tr>
                <TableCell colSpan={13}>
                  <EmptyState>
                    <AlertCircle size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                    <p>No se encontraron registros con los filtros aplicados.</p>
                  </EmptyState>
                </TableCell>
              </tr>
            ) : (
              registros.map((registro) => (
                <TableRow key={registro.id} style={{
                  background: registro.editado_por_mi ? 'rgba(16, 185, 129, 0.05)' : 'inherit'
                }}>
                  <UserCell style={{
                    background: registro.editado_por_mi ? 'rgba(16, 185, 129, 0.1)' : undefined
                  }}>
                    <User size={16} />
                    {proveedor?.username || proveedor?.razon_social || 'Usuario'}
                    {registro.editado_por_mi && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '10px',
                        padding: '2px 6px',
                        background: '#10b981',
                        color: 'white',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </span>
                    )}
                  </UserCell>
                  <TableCell>{registro.origen_ciudad}</TableCell>
                  <TableCell>{registro.destino_ciudad}</TableCell>
                  <TableCell>{registro.tipo_vehiculo}</TableCell>
                  <TableCell>{registro.tipo_carga}</TableCell>
                  <TableCell>{registro.configuracion}</TableCell>
                  <TableCell>{registro.valor_sicetac?.toLocaleString()}</TableCell>
                  <TableCell>{registro.viajes_mes || '-'}</TableCell>
                  <TableCell>{registro.ton_mes ? registro.ton_mes.toLocaleString() : '-'}</TableCell>
                  
                  {/* Columna editable: % Margen - Solo editable si hay valor SICETAC */}
                  <EditableCell>
                    {registro.valor_sicetac && registro.valor_sicetac > 0 ? (
                      // Caso con valor SICETAC: mostrar input de % Margen
                      editingRow?.id === registro.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <EditInput
                            type="number"
                            value={editForm.porcentaje_margen}
                            onChange={(e) => handleEditFormChange('porcentaje_margen', e.target.value)}
                            placeholder="% Margen"
                            step="0.1"
                            style={{ width: '80px' }}
                          />
                          <span style={{ fontSize: '0.9em' }}>%</span>
                        </div>
                      ) : (
                        <span>
                          {registro.valor_de_tarifa && registro.valor_sicetac 
                            ? `${((registro.valor_de_tarifa / registro.valor_sicetac - 1) * 100).toFixed(2)}%`
                            : '0%'}
                        </span>
                      )
                    ) : (
                      // Caso sin valor SICETAC: mostrar N/A
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>
                    )}
                  </EditableCell>
                  
                  {/* Columna: Objetivo */}
                  <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>
                    {registro.objetivo ? (() => {
                      const val = parseFloat(registro.objetivo);
                      if (isNaN(val)) return '-';
                      // Si el valor es menor a 1, asumimos que está en formato decimal (0.15 = 15%)
                      const porcentaje = val < 1 ? (val * 100).toFixed(2) : val.toFixed(2);
                      return `${porcentaje}%`;
                    })() : '-'}
                  </TableCell>
                  
                  {/* Columna Valor Tarifa - Editable directamente si NO hay valor SICETAC */}
                  <TableCell>
                    {registro.valor_sicetac && registro.valor_sicetac > 0 ? (
                      // Caso con SICETAC: mostrar valor calculado
                      editingRow?.id === registro.id ? (
                        <span style={{ 
                          fontWeight: 'bold',
                          color: editForm.porcentaje_margen ? '#10b981' : '#6b7280'
                        }}>
                          ${editForm.porcentaje_margen 
                            ? calcularValorTarifa(registro.valor_sicetac || 0, parseFloat(editForm.porcentaje_margen) || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                            : '0'}
                        </span>
                      ) : (
                        <span>{registro.valor_de_tarifa?.toLocaleString() || '0'}</span>
                      )
                    ) : (
                      // Caso sin SICETAC: input directo para Valor Tarifa
                      editingRow?.id === registro.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>$</span>
                          <EditInput
                            type="number"
                            value={editForm.valor_tarifa_directo}
                            onChange={(e) => handleEditFormChange('valor_tarifa_directo', e.target.value)}
                            placeholder="Valor Tarifa"
                            style={{ width: '100px' }}
                          />
                        </div>
                      ) : (
                        <span>{registro.valor_de_tarifa?.toLocaleString() || '0'}</span>
                      )
                    )}
                  </TableCell>
                  
                  {/* Columna editable: Comentarios */}
                  <EditableCell>
                    {editingRow?.id === registro.id ? (
                      <EditTextarea
                        value={editForm.comentarios}
                        onChange={(e) => handleEditFormChange('comentarios', e.target.value)}
                        placeholder="Ingrese comentarios..."
                        rows={2}
                      />
                    ) : (
                      <span title={registro.comentarios}>
                        {registro.comentarios || '-'}
                      </span>
                    )}
                  </EditableCell>
                  
                  {/* Columna de Acciones */}
                  <ActionsCell>
                    {LICITACION_CERRADA ? (
                      <span 
                        style={{ 
                          color: '#9ca3af', 
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                        title="Licitación cerrada - Solo lectura"
                      >
                        <Eye size={14} />
                      </span>
                    ) : editingRow?.id === registro.id ? (
                      <>
                        <EditActionButton 
                          variant="save" 
                          onClick={handleEditSave}
                          disabled={isUpdating}
                          title="Guardar cambios"
                        >
                          <Save size={14} />
                        </EditActionButton>
                        <EditActionButton 
                          variant="cancel" 
                          onClick={handleEditCancel}
                          disabled={isUpdating}
                          title="Cancelar edición"
                        >
                          <X size={14} />
                        </EditActionButton>
                      </>
                    ) : (
                      <EditActionButton 
                        onClick={() => handleEditStart(registro)}
                        title="Editar % margen y comentarios"
                      >
                        <Edit size={14} />
                      </EditActionButton>
                    )}
                  </ActionsCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Información de paginación */}
      {registros.length > 0 && (
        <div style={{ 
          marginTop: '20px', 
          textAlign: 'center', 
          color: '#6b7280',
          fontSize: '14px'
        }}>
          Mostrando {registros.length} de {pagination.total_records} registros
          {pagination.total_pages > 1 && (
            <span> - Página {pagination.current_page} de {pagination.total_pages}</span>
          )}
        </div>
      )}

      {/* Controles de paginación */}
      {pagination.total_pages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          padding: '20px'
        }}>
          {/* Primera página */}
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.current_page === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: pagination.current_page === 1 ? '#f3f4f6' : '#ffffff',
              color: pagination.current_page === 1 ? '#9ca3af' : '#374151',
              cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Primera página"
          >
            <ChevronsLeft size={16} />
          </button>

          {/* Página anterior */}
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: pagination.current_page === 1 ? '#f3f4f6' : '#ffffff',
              color: pagination.current_page === 1 ? '#9ca3af' : '#374151',
              cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Página anterior"
          >
            <ChevronLeft size={16} />
            Anterior
          </button>

          {/* Números de página */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {(() => {
              const currentPage = pagination.current_page;
              const totalPages = pagination.total_pages;
              const pages = [];
              
              // Mostrar páginas alrededor de la actual
              const startPage = Math.max(1, currentPage - 2);
              const endPage = Math.min(totalPages, currentPage + 2);
              
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: i === currentPage ? '#3b82f6' : '#ffffff',
                      color: i === currentPage ? '#ffffff' : '#374151',
                      cursor: 'pointer',
                      fontWeight: i === currentPage ? 'bold' : 'normal',
                      minWidth: '40px'
                    }}
                  >
                    {i}
                  </button>
                );
              }
              
              return pages;
            })()}
          </div>

          {/* Página siguiente */}
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.total_pages}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: pagination.current_page === pagination.total_pages ? '#f3f4f6' : '#ffffff',
              color: pagination.current_page === pagination.total_pages ? '#9ca3af' : '#374151',
              cursor: pagination.current_page === pagination.total_pages ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Página siguiente"
          >
            Siguiente
            <ChevronRight size={16} />
          </button>

          {/* Última página */}
          <button
            onClick={() => handlePageChange(pagination.total_pages)}
            disabled={pagination.current_page === pagination.total_pages}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: pagination.current_page === pagination.total_pages ? '#f3f4f6' : '#ffffff',
              color: pagination.current_page === pagination.total_pages ? '#9ca3af' : '#374151',
              cursor: pagination.current_page === pagination.total_pages ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Última página"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      )}
    </RutasContainer>
  );
};

export default RutasGranelModule;